import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Clock, User, Play, Headphones } from "lucide-react";
import api from "../../api/axios";
import { stripHtml, truncate } from "../../utils/text";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";

export default function WisdomSection() {
    const { user } = useAuth();
    const { t } = useLanguage();
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchFeatured();
    }, [user]);

    const fetchFeatured = async () => {
        try {
            let url = "/articles/featured";
            if (user?.seeker?.categories?.length > 0) {
                const catIds = user.seeker.categories.map(c => c.categoryId).join(",");
                url += `?prioritize=${catIds}`;
            }
            const res = await api.get(url);
            setArticles(res.data);
        } catch (err) {
            console.error("Failed to fetch featured scrolls", err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return (
        <div className="py-20 text-center text-gray-400 animate-pulse">
            {t('wisdom_loading')}
        </div>
    );

    if (articles.length === 0) return null;

    return (
        <section className="py-24 bg-white dark:bg-gray-950 px-6 overflow-hidden">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
                    <div className="space-y-4">
                        <h2 className="text-3xl md:text-5xl font-display font-bold text-gray-900 dark:text-gray-100 italic">
                            {t('wisdom_title')}
                        </h2>
                        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl font-light">
                            {t('wisdom_desc')}
                        </p>
                    </div>
                    <Link 
                        to="/explore" 
                        className="group inline-flex items-center gap-2 text-brand-blue font-medium hover:underline"
                    >
                        {t('wisdom_cta')} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                </div>

                <div className="flex md:grid overflow-x-auto md:overflow-visible pb-8 pt-4 snap-x snap-mandatory md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 -mx-6 px-6 md:mx-0 md:px-0 no-scrollbar">
                    {articles.map((article, idx) => (
                        <motion.div
                            key={article.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.1 }}
                            className="min-w-[85vw] sm:min-w-[320px] md:min-w-0 snap-center group flex flex-col bg-gray-50 dark:bg-gray-900/50 rounded-3xl overflow-hidden border border-gray-100 dark:border-gray-800 hover:border-blue-100 dark:hover:border-blue-900/30 hover:shadow-2xl hover:shadow-blue-900/5 transition-all duration-500"
                        >
                            {/* Image/Media Preview */}
                            <div className="relative h-64 overflow-hidden bg-gray-200 dark:bg-gray-800">
                                {article.coverImage ? (
                                    <img 
                                        src={article.coverImage} 
                                        alt={article.title}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-300 dark:text-gray-700">
                                        <BookOpen size={48} strokeWidth={1} />
                                    </div>
                                )}
                                
                                {/* Media Badge */}
                                <div className="absolute top-4 right-4 flex gap-2">
                                    {article.videoUrl && (
                                        <div className="p-2 bg-white/90 dark:bg-black/60 backdrop-blur-md rounded-full text-red-500 shadow-sm">
                                            <Play size={14} fill="currentColor" />
                                        </div>
                                    )}
                                    {article.audioUrl && (
                                        <div className="p-2 bg-white/90 dark:bg-black/60 backdrop-blur-md rounded-full text-brand-blue shadow-sm">
                                            <Headphones size={14} />
                                        </div>
                                    )}
                                </div>

                                {article.category && (
                                    <div className="absolute bottom-4 left-4">
                                        <span className="bg-white/90 dark:bg-black/60 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border border-white/20">
                                            {article.category.label}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Content */}
                            <div className="p-8 flex-1 flex flex-col">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3 group-hover:text-brand-blue transition-colors leading-snug">
                                    <Link to={`/article/${article.slug || article.id}`}>{article.title}</Link>
                                </h3>

                                
                                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-3 mb-6 font-light leading-relaxed">
                                    {truncate(stripHtml(article.excerpt || article.content), 150)}
                                </p>


                                <div className="mt-auto pt-6 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center overflow-hidden">
                                            {((article.author?.name?.toLowerCase().includes('joshi ayush')) || (article.author?.name?.toLowerCase().includes('ayush joshi')) || (article.author?.name === 'All-Seer')) ? (
                                                <img src="/favicon/favicon.svg" alt="" className="w-4 h-4 object-contain" />
                                            ) : (
                                                <User size={12} className="text-gray-400" />
                                            )}
                                        </div>
                                        <span className="text-xs font-medium text-gray-500 italic">
                                            {(article.author?.name?.toLowerCase().includes('joshi ayush') || article.author?.name?.toLowerCase().includes('ayush joshi')) ? 'All-Seer' : (article.author?.name || 'All-Seer')}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1 text-[10px] text-gray-400 uppercase tracking-tighter">
                                        <Clock size={10} />
                                        {new Date(article.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
